import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useWizard, WizardStep } from '../../../contexts/WizardContext';
import { useHistory } from 'react-router-dom';
import Layout from '../../Layout';
import Button from '../../shared/buttonComponents/Button';
import SelectBox from '../../shared/select-boxes/SelectBox';
import MultiSelectPopup from '../MultiSelectPopup';
import PaymentsPopupMobile from './PaymentMethodsPopup';
import ButtonBack from '../../shared/buttonComponents/ButtonBack';
import PaymentsPopup from './PaymentsPopup';
import { useMediaQuery } from '../../../contexts/MediaQueryContext';

interface RegionsListItem {
  name: string;
  icon: string;
  gallery: any;
  selected: boolean;
}

interface PaymentMethods {
  selected: boolean;
  current: string | undefined;
  methodsGallery: string[][] | any;
}

export const RegionsPage: FC = () => {
  const {
    changeStep,
    data,
    updateStepData,
    staticData
  } = useWizard();

  const { onMobile } = useMediaQuery();

  const regionsList = useMemo((): RegionsListItem[] => {
    return staticData.solutions.map((solution) => ({
      name: solution.name,
      gallery: solution.gallery,
      icon: `regions/${solution.name.toLocaleLowerCase().replace(/\\s/g, '-')}`,
      selected: false
    }));
  }, [staticData.solutions]);

  const [hovered, setHovered] = useState<string>('');
  const [regions, setRegions] = useState<RegionsListItem[]>(regionsList);
  const [firstRun, setFirstRun] = useState<boolean>(true);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [showMethods, setShowMethods] = useState<PaymentMethods>({
    selected: false,
    current: undefined,
    methodsGallery: null
  });

  const solutions = staticData.solutions;

  useEffect(() => {
    if (!data.solutions) {
      return;
    }

    setRegions((state) => state.map((region) => {
      region.selected = data.solutions.includes(region.name);

      return region;
    }));
  }, [data.solutions]);


  const handleSelect = useCallback((name, selected, fromMobile = false) => {
    if (onMobile && !fromMobile) {
      return;
    }

    if (selected) {
      const data = regions
        .filter(r => r.selected)
        .map(r => r.name);

      data.push(name);

      updateStepData({
        solutions: data
      });
    } else {
      const data = regions
        .filter(r => r.selected && r.name !== name)
        .map(r => r.name);

      updateStepData({
        solutions: data
      });
    }

    if (firstRun) {
      setFirstRun(false);

      setTimeout(() => {
        setShowPopup(true);
      }, 500);
    }

  }, [regions, updateStepData, firstRun, onMobile]);

  const getPaymentsSolution = useCallback((name) => {
    const solution = solutions.find((s) => s.name === name);

    if (solution) {
      if (solution.image) {
        return [solution.image];
      }

      if (solution.gallery) {
        return solution.gallery.map((imageData) => {
          const [url] = imageData;
          return url;
        });
      }
    }

    return [];
  }, [solutions]);

  let history = useHistory();

  useEffect(() => {

    return () => {
      if (history.action === 'POP') {
        changeStep(WizardStep.HOME);
      }
    };
  });

  return (
    <Layout>

      {showPopup && (
        <MultiSelectPopup onClick={() => setShowPopup(false)}/>
      )}

      <div className="regions-page-container">
        <div className="regions-page-clouds">
          <img
            src={`${process.env.PUBLIC_URL}/assets/images/clouds(0.17-opacity).png`}
            alt="clouds"
          />
        </div>
        <div className='plane-container-mobile'/>
        <div className="regions-page-title-container">
          <div className="regions-page-title">
            <div className="secondary-title">Let’s get started!</div>
            <div className="main-title">
              <p className='desktop-title'>Which payment<br/>solutions do you require?</p>
            </div>
            <div className="main-title">
              <p className='mobile-title'>Which payment<br/>solutions do<br/>you require?</p>
            </div>
            <div className="secondary-title-2">( you can select multiple regions )</div>
          </div>
          <div className="regions-page-button">
            <Button
              disabled={!regions.filter(r => r.selected).length}
              onClick={() => changeStep(WizardStep.CONTINENT)}
              title="Next"
            />
          </div>
        </div>

        <div className="regions-page-list">
          {regions.map((region, index) => (
            <div
              onClick={() => handleSelect(region.name, !region.selected, false)}
              key={index}
              onMouseOver={() => setHovered(region.name)}
              onMouseLeave={() => setHovered('')}
            >
              <SelectBox multi={true} isSelected={region.selected} icon={region.icon} title={region.name}>
                {hovered === region.name && showMethods && (
                  <PaymentsPopup
                    region={region.name}
                    images={getPaymentsSolution(region.name)}
                  />
                )}
                <div className='card-btn-group-mobile'>
                  {region.selected
                    ? <div
                      onClick={() => handleSelect(region.name, !region.selected, true)}
                      className='default-button'
                    >
                      remove
                    </div>
                    : <div
                      onClick={() => handleSelect(region.name, !region.selected, true)}
                      className='default-button'
                    >
                      select
                    </div>
                  }
                  <div
                    onClick={() => setShowMethods({
                      selected: true,
                      current: region.name,
                      methodsGallery: region.gallery
                    })}
                    className='default-button btn-secondary'>see methods
                  </div>
                </div>
              </SelectBox>
            </div>
          ))}
        </div>
        <div className='btn-back-desktop'>
          <ButtonBack
            disabled={false}
            onClick={() => changeStep(WizardStep.HOME)}
            title="Back"
          />
        </div>

        <div className="regions-page-button-mobile">
          {/*<div className="secondary-title-2">(you can select multiple regions)</div>*/}
          <Button
            disabled={!regions.filter(r => r.selected).length}
            onClick={() => changeStep(WizardStep.CONTINENT)}
            title="Next"
          />
          <ButtonBack
            disabled={false}
            onClick={() => changeStep(WizardStep.HOME)}
            title="Back"
          />
        </div>
      </div>

      {showMethods.current && showMethods.selected && (
        <PaymentsPopupMobile
          region={showMethods.current}
          images={getPaymentsSolution(showMethods.current)}
          onClose={() => setShowMethods({
            selected: false,
            current: showMethods?.current,
            methodsGallery: showMethods.current
          })}
        />
      )}

    </Layout>
  );
};
